// src/utils/checkinQueue.js
// localStorage retry queue for kiosk sign-ins. A guest's completed check-in
// must never be lost to flaky open-house wifi: saves that fail (or hang —
// Firestore never rejects while offline, it just waits) are queued here and
// retried on reconnect, on the next sign-in, and on kiosk mount.

export const QUEUE_KEY = "ohCheckinQueue";

// Entries are { qid, payload } so a flush can remove exactly the entry it
// saved even if the queue changed underneath it.
export function loadQueue(storage) {
  try {
    const raw = storage.getItem(QUEUE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

export function enqueueCheckin(storage, payload) {
  const q = loadQueue(storage);
  q.push({ qid: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, payload });
  storage.setItem(QUEUE_KEY, JSON.stringify(q));
}

// Only one flush at a time: mount, the `online` event, and post-save flushes
// can all fire together, and two concurrent flushes would double-save.
let flushing = false;

// Saves oldest-first; stops at the first failure (if one write fails the
// rest will usually fail too, and order preserves sign-in time). Re-reads
// the queue every iteration so entries enqueued mid-flush are never lost.
export async function flushCheckinQueue(storage, saveFn) {
  if (flushing) return { saved: 0, remaining: loadQueue(storage).length, busy: true };
  flushing = true;
  let saved = 0;
  try {
    for (;;) {
      const q = loadQueue(storage);
      if (q.length === 0) break;
      const head = q[0];
      let ok = false;
      try {
        const r = await saveFn(head.payload);
        ok = !!r && r.success !== false;
      } catch {
        ok = false;
      }
      if (!ok) break;
      const now = loadQueue(storage);
      storage.setItem(QUEUE_KEY, JSON.stringify(now.filter((e) => e.qid !== head.qid)));
      saved += 1;
    }
  } finally {
    flushing = false;
  }
  return { saved, remaining: loadQueue(storage).length };
}
